import { creditForCut } from "./bank-credits";
import { cardPaymentEventId, cardPaymentRequirement } from "./card-payments";
import { paymentTimingForCut, planCardPaymentTiming, type CardPaymentTimingInput } from "./card-payment-timing";
import { correctionFingerprint } from "./movement-correction-store";
import { assertAllocationsWithinNu } from "./periods";
import type { FinancialSnapshot, Movement } from "./types";

interface TimingContext {
  snapshot: FinancialSnapshot;
  movements: Movement[];
  settings: Record<string, unknown>;
  paidIds: string[];
}

async function readContext(uid: string): Promise<TimingContext> {
  const [{ collection, doc, getDocFromServer, getDocsFromServer }, { firestore }] = await Promise.all([
    import("firebase/firestore"), import("./firebase"),
  ]);
  const root = (collectionName: string, id: string) => doc(firestore, "users", uid, collectionName, id);
  const [state, rows, settings, payments] = await Promise.all([
    getDocFromServer(root("snapshots", "current")),
    getDocsFromServer(collection(firestore, "users", uid, "movements")),
    getDocFromServer(root("views", "settings")),
    getDocsFromServer(collection(firestore, "users", uid, "cardPaymentEvents")),
  ]);
  if (!state.exists()) throw new Error("No encontré la fotografía financiera.");
  return {
    snapshot: state.data() as FinancialSnapshot,
    movements: rows.docs.map((row) => ({ ...row.data(), id: row.id } as Movement)).sort((a, b) => a.id.localeCompare(b.id)),
    settings: settings.data() ?? {},
    paidIds: payments.docs.map((row) => row.id).sort(),
  };
}

function previewPlan(context: TimingContext, input: CardPaymentTimingInput, operationId: string) {
  const credit = context.snapshot.bankCredits?.find((item) => item.id === input.bankCreditId);
  if (!credit) throw new Error("No encontré la bonificación bancaria.");
  const cards = (context.settings.data as Record<string, unknown> | undefined)?.tarjetas;
  const configured = Array.isArray(cards)
    ? cards.find((card) => (card.medio ?? card.nombre) === credit.card) as Record<string, unknown> | undefined
    : undefined;
  const cutDay = Number(configured?.diaCorte);
  const adjustment = planCardPaymentTiming(context.snapshot, input, operationId, cutDay, context.paidIds);
  const movements = context.movements.filter((movement) => movement.kind === "expense" && movement.paymentMethod === credit.card && movement.cardPeriod === input.paymentPeriod);
  const reserve = context.snapshot.cardPayments.find((item) => item.name === credit.card);
  const inherited = reserve?.unassignedPeriod === input.paymentPeriod ? Math.max(0, reserve.unassignedCents ?? 0) : 0;
  if (inherited) throw new Error("Este corte incluye una reserva heredada sin detalle y necesita revisión individual.");
  if (!movements.length && !inherited) throw new Error("El corte anterior no tiene deuda registrada.");
  const remainder = creditForCut(context.snapshot.bankCredits ?? [], credit.card, input.paymentPeriod).remainderCents;
  const previousTiming = paymentTimingForCut(context.snapshot.cardPaymentTimingAdjustments ?? [], credit.card, input.paymentPeriod);
  const before = cardPaymentRequirement(movements, inherited, remainder, previousTiming);
  if (before.ownPendingCents > 0) throw new Error("Primero debe estar cubierta la parte propia del corte anterior.");
  if (adjustment.amountCents >= before.exactPaymentCents) throw new Error("El ajuste dejaría el pago del corte anterior en cero o negativo.");
  return {
    adjustment,
    paymentBeforeCents: before.exactPaymentCents,
    paymentAfterCents: before.exactPaymentCents - adjustment.amountCents,
  };
}

function validOperationId(operationId: string): boolean {
  return /^paymenttiming_[a-zA-Z0-9-]+$/.test(operationId);
}

export async function previewCardPaymentTiming(uid: string, input: CardPaymentTimingInput, operationId: string) {
  if (!validOperationId(operationId)) throw new Error("Identificador de ajuste inválido.");
  const context = await readContext(uid);
  const plan = previewPlan(context, input, operationId);
  return {
    token: await correctionFingerprint({ context, input, operationId }),
    card: plan.adjustment.card,
    sourcePeriod: plan.adjustment.sourcePeriod,
    paymentPeriod: plan.adjustment.paymentPeriod,
    amountCents: plan.adjustment.amountCents,
    paymentBeforeCents: plan.paymentBeforeCents,
    paymentAfterCents: plan.paymentAfterCents,
    nuDeltaCents: 0,
    reserveDeltaCents: 0,
  };
}

export async function commitCardPaymentTiming(uid: string, input: CardPaymentTimingInput, operationId: string, token: string) {
  if (!validOperationId(operationId) || !token) throw new Error("Revisa primero el ajuste entre cortes.");
  const discovered = await readContext(uid);
  const [{ doc, runTransaction }, { firestore }] = await Promise.all([
    import("firebase/firestore"), import("./firebase"),
  ]);
  const root = (collectionName: string, id: string) => doc(firestore, "users", uid, collectionName, id);
  const credit = discovered.snapshot.bankCredits?.find((item) => item.id === input.bankCreditId);
  if (!credit) throw new Error("No encontré la bonificación bancaria.");
  const relevantRows = discovered.movements.filter((item) => item.paymentMethod === credit.card
    && (item.cardPeriod === credit.period || item.cardPeriod === input.paymentPeriod));
  return runTransaction(firestore, async (transaction) => {
    const [operation, state, settings, priorPayment, sourcePayment, creditRecord, ...rows] = await Promise.all([
      transaction.get(root("operations", operationId)),
      transaction.get(root("snapshots", "current")),
      transaction.get(root("views", "settings")),
      transaction.get(root("cardPaymentEvents", cardPaymentEventId(credit.card, input.paymentPeriod))),
      transaction.get(root("cardPaymentEvents", cardPaymentEventId(credit.card, credit.period))),
      transaction.get(root("cardReserveAdjustments", credit.id)),
      ...relevantRows.map((item) => transaction.get(root("movements", item.id))),
    ]);
    if (!state.exists()) throw new Error("No encontré la fotografía financiera.");
    const requestHash = await correctionFingerprint(input);
    if (operation.exists()) {
      if (operation.data().requestHash !== requestHash) throw new Error("El identificador ya se usó con otros datos.");
      return { snapshot: state.data() as FinancialSnapshot, result: { ...operation.data().result, alreadyApplied: true } };
    }
    if (priorPayment.exists() || sourcePayment.exists() || !creditRecord.exists() || rows.some((row) => !row.exists())) {
      throw new Error("Los cortes o la bonificación cambiaron. Vuelve a revisar el ajuste.");
    }
    const context: TimingContext = {
      snapshot: state.data() as FinancialSnapshot,
      movements: discovered.movements.map((item) => {
        const row = rows.find((document) => document.id === item.id);
        return row ? { ...row.data(), id: row.id } as Movement : item;
      }),
      settings: settings.data() ?? {},
      paidIds: discovered.paidIds,
    };
    const currentCredit = context.snapshot.bankCredits?.find((item) => item.id === input.bankCreditId);
    const recordedCredit = creditRecord.data();
    if (!currentCredit || recordedCredit.amountCents !== currentCredit.amountCents
      || recordedCredit.period !== currentCredit.period || recordedCredit.card !== currentCredit.card
      || (recordedCredit.allocations ?? []).length !== currentCredit.allocations.length
      || (recordedCredit.allocations ?? []).some((allocation: { movementId: string; amountCents: number }, index: number) =>
        allocation.movementId !== currentCredit.allocations[index]?.movementId
        || allocation.amountCents !== currentCredit.allocations[index]?.amountCents)) {
      throw new Error("La bonificación cambió. Vuelve a revisar el ajuste.");
    }
    if (await correctionFingerprint({ context, input, operationId }) !== token) {
      throw new Error("Los datos cambiaron desde la revisión. Vuelve a revisar el ajuste.");
    }
    const plan = previewPlan(context, input, operationId);
    const now = plan.adjustment.createdAt;
    const next = assertAllocationsWithinNu({
      ...context.snapshot,
      cardPaymentTimingAdjustments: [...(context.snapshot.cardPaymentTimingAdjustments ?? []), plan.adjustment],
      updatedAt: now,
    });
    const result = {
      ok: true,
      card: plan.adjustment.card,
      sourcePeriod: plan.adjustment.sourcePeriod,
      paymentPeriod: plan.adjustment.paymentPeriod,
      amountCents: plan.adjustment.amountCents,
      paymentBeforeCents: plan.paymentBeforeCents,
      paymentAfterCents: plan.paymentAfterCents,
      nuDeltaCents: 0,
      reserveDeltaCents: 0,
    };
    transaction.set(root("snapshots", "current"), next);
    transaction.set(root("operations", operationId), {
      id: operationId,
      kind: "card_payment_timing",
      createdAt: now,
      requestHash,
      input,
      adjustment: plan.adjustment,
      result,
    });
    return { snapshot: next, result };
  });
}
